'use client'

import React, {useEffect, useMemo, useState} from 'react'
import {usePathname} from 'next/navigation'
import {ClerkProvider, SignedOutRequestContext, useAuth} from '@/lib/clerk-nextjs'
import {ensureClerkEnv} from '@/lib/clerk-env'
import {clerkTaskUrls} from '@/lib/clerk-task-routing'
import {clerkLocalization} from '@/lib/clerk-localization'
import {isCodexBrowserModeEnabled} from '@/lib/codex-browser-mode'
import {PublicEntryLoading} from '@fitro/customer-features/components/client/public-entry/PublicEntryLoading'
import {resolvePublicSitePage} from '@fitro/customer-features/components/client/public-entry/publicSiteNavigation'
import {isPublicTenantEntryPath} from '@fitro/customer-features/lib/tenant-seo-policy'

const AUTH_PROVIDER_BOOT_TIMEOUT_MS = 4000

type ClerkGateProps = {
    children: React.ReactNode
    /** The request carried no Clerk client session. */
    signedOutRequest?: boolean
}

export function ClerkRuntimeGate({children, signedOutRequest = false}: ClerkGateProps) {
    const pathname = usePathname() ?? '/'
    // A signed-out visitor sees the same public page before and after Clerk loads, so it renders without waiting.
    const publicSignedOut = signedOutRequest && isPublicTenantEntryPath(pathname)
    const {isLoaded} = useAuth()
    const [timedOut, setTimedOut] = useState(false)

    useEffect(() => {
        if (isLoaded) {
            setTimedOut(false)
            return
        }

        const timeoutId = window.setTimeout(() => {
            setTimedOut(true)
        }, AUTH_PROVIDER_BOOT_TIMEOUT_MS)

        return () => {
            window.clearTimeout(timeoutId)
        }
    }, [isLoaded])

    if (!isLoaded && !timedOut && !publicSignedOut) {
        return <PublicEntryLoading page={resolvePublicSitePage(pathname)} />
    }

    return <SignedOutRequestContext.Provider value={publicSignedOut}>{children}</SignedOutRequestContext.Provider>
}

function RuntimeClerkAppProvider({children, signedOutRequest}: ClerkGateProps) {
    ensureClerkEnv()

    const signInUrl = process.env.NEXT_PUBLIC_CLERK_SIGN_IN_URL || '/sign-in'
    const signUpUrl = process.env.NEXT_PUBLIC_CLERK_SIGN_UP_URL || '/sign-up'

    const appearance = useMemo(() => ({
        variables: {
            colorPrimary: '#6366f1',
            colorText: 'var(--text, #111827)',
            colorBackground: 'var(--surface, #ffffff)',
            colorInputBackground: 'var(--surface, #ffffff)',
            colorInputText: 'var(--text, #111827)',
            colorNeutral: '#6b7280',
        },
        elements: {
            card: 'rounded-2xl border border-[var(--border)] shadow-xl bg-white dark:bg-gray-900',
            headerTitle: 'text-h4 font-semibold text-gray-900 dark:text-gray-100',
            headerSubtitle: 'text-body text-gray-500 dark:text-gray-400',
            formButtonPrimary: 'bg-[linear-gradient(16deg,var(--fitro-brand-gradient-primary-600)_0%,var(--fitro-brand-gradient-secondary-500)_100%)] hover:brightness-[1.04] text-white rounded-lg h-10 shadow-[0_14px_28px_rgba(57,98,198,0.24)]',
            formFieldInput: 'rounded-lg border-[var(--border)] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
            footerAction__signIn: 'text-primary-600 hover:text-primary-700',
            footerAction__signUp: 'text-primary-600 hover:text-primary-700',
            alternativeMethodsBlockButton: 'rounded-lg border-[var(--border)]',
        },
    }), [])

    return (
        <ClerkProvider
            appearance={appearance}
            localization={clerkLocalization}
            signInUrl={signInUrl}
            signUpUrl={signUpUrl}
            taskUrls={clerkTaskUrls}
        >
            <ClerkRuntimeGate signedOutRequest={signedOutRequest}>
                {children}
            </ClerkRuntimeGate>
        </ClerkProvider>
    )
}

export function ClerkAppProvider({children, signedOutRequest}: ClerkGateProps) {
    if (isCodexBrowserModeEnabled()) {
        return <>{children}</>
    }

    return <RuntimeClerkAppProvider signedOutRequest={signedOutRequest}>{children}</RuntimeClerkAppProvider>
}
